import { 
  DashboardLayout, 
  DashboardWidget, 
  WidgetDefinition, 
  WidgetConfig,
  DashboardLayoutService 
} from '../types/dashboard';

export class DashboardService implements DashboardLayoutService {
  private storageKey = 'aalaw-dashboard-layout';

  async getUserLayout(userId: string): Promise<DashboardLayout> {
    try {
      const stored = localStorage.getItem(`${this.storageKey}-${userId}`);
      if (stored) {
        return JSON.parse(stored);
      }
      return this.getDefaultLayout();
    } catch (error) {
      console.error('Error loading user layout:', error);
      return this.getDefaultLayout();
    }
  }

  async saveUserLayout(userId: string, layout: DashboardLayout): Promise<void> {
    try {
      const updatedLayout = {
        ...layout,
        lastModified: new Date().toISOString(),
        version: (layout.version || 0) + 1,
      };
      localStorage.setItem(`${this.storageKey}-${userId}`, JSON.stringify(updatedLayout));
    } catch (error) {
      console.error('Error saving user layout:', error);
      throw error;
    }
  }

  async resetToDefault(userId: string): Promise<DashboardLayout> {
    const defaultLayout = this.getDefaultLayout();
    await this.saveUserLayout(userId, defaultLayout);
    return defaultLayout;
  }

  getAvailableWidgets(): WidgetDefinition[] {
    return [
      {
        type: 'call-metrics',
        name: 'Call Metrics',
        description: 'Display call statistics, trends, and performance data',
        icon: 'Phone',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 12, height: 6 },
        defaultConfig: {
          showTrends: true,
          chartType: 'donut',
          refreshInterval: 300000,
          showComparisons: true,
        },
        configSchema: [
          {
            key: 'showTrends',
            label: 'Show Trends',
            type: 'boolean',
            defaultValue: true,
            description: 'Display trend charts over time',
          },
          {
            key: 'chartType',
            label: 'Chart Type',
            type: 'select',
            options: [
              { value: 'bar', label: 'Bar Chart' },
              { value: 'line', label: 'Line Chart' },
              { value: 'pie', label: 'Pie Chart' },
              { value: 'donut', label: 'Donut Chart' },
            ],
            defaultValue: 'donut',
          },
          {
            key: 'refreshInterval',
            label: 'Refresh Interval (ms)',
            type: 'number',
            defaultValue: 300000,
            description: 'How often to refresh data (milliseconds)',
          },
        ],
      },
      {
        type: 'inquiry-summary',
        name: 'Inquiry Summary',
        description: 'Show inquiry pipeline and lead scores',
        icon: 'FileText',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {
          showLeadScores: true,
          sortBy: 'leadScore',
          maxItems: 10,
        },
        configSchema: [
          {
            key: 'showLeadScores',
            label: 'Show Lead Scores',
            type: 'boolean',
            defaultValue: true,
          },
          {
            key: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
              { value: 'date', label: 'Date' },
              { value: 'leadScore', label: 'Lead Score' },
              { value: 'status', label: 'Status' },
            ],
            defaultValue: 'leadScore',
          },
          {
            key: 'maxItems',
            label: 'Max Items',
            type: 'number',
            defaultValue: 10,
          },
        ],
      },
      {
        type: 'recent-messages',
        name: 'Recent Messages',
        description: 'Display recent message activity with unread indicators',
        icon: 'MessageSquare',
        defaultSize: { width: 4, height: 4 },
        minSize: { width: 3, height: 3 },
        maxSize: { width: 6, height: 6 },
        defaultConfig: {
          conversationLimit: 5,
          showUnreadOnly: false,
          autoRefresh: true,
        },
        configSchema: [
          {
            key: 'conversationLimit',
            label: 'Conversation Limit',
            type: 'number',
            defaultValue: 5,
          },
          {
            key: 'showUnreadOnly',
            label: 'Show Unread Only',
            type: 'boolean',
            defaultValue: false,
          },
          {
            key: 'autoRefresh',
            label: 'Auto Refresh',
            type: 'boolean',
            defaultValue: true,
          },
        ],
      },
      {
        type: 'activity-feed',
        name: 'Activity Feed',
        description: 'Timeline of recent system activities and updates',
        icon: 'Activity',
        defaultSize: { width: 4, height: 5 },
        minSize: { width: 3, height: 4 },
        maxSize: { width: 6, height: 8 },
        defaultConfig: {
          timeRange: 'week',
          maxActivities: 15,
        },
        configSchema: [
          {
            key: 'timeRange',
            label: 'Time Range',
            type: 'select',
            options: [
              { value: 'today', label: 'Today' },
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
            ],
            defaultValue: 'week',
          },
          {
            key: 'maxActivities',
            label: 'Max Activities',
            type: 'number',
            defaultValue: 15,
          },
        ],
      },
      {
        type: 'staff-performance',
        name: 'Staff Performance',
        description: 'Individual and team performance metrics',
        icon: 'Users',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {
          comparisonPeriod: 'month',
        },
        configSchema: [
          {
            key: 'comparisonPeriod',
            label: 'Comparison Period',
            type: 'select',
            options: [
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'quarter', label: 'This Quarter' },
            ],
            defaultValue: 'month',
          },
        ],
      },
      {
        type: 'quick-actions',
        name: 'Quick Actions',
        description: 'Shortcuts to common tasks and operations',
        icon: 'Zap',
        defaultSize: { width: 3, height: 3 },
        minSize: { width: 2, height: 2 },
        maxSize: { width: 4, height: 4 },
        defaultConfig: {
          actions: ['new-inquiry', 'new-case', 'new-call-log', 'new-appointment'],
        },
        configSchema: [
          {
            key: 'actions',
            label: 'Available Actions',
            type: 'multiselect',
            options: [
              { value: 'new-inquiry', label: 'New Inquiry' },
              { value: 'new-case', label: 'New Case' },
              { value: 'new-call-log', label: 'New Call Log' },
              { value: 'new-appointment', label: 'New Appointment' },
              { value: 'new-task', label: 'New Task' },
              { value: 'new-document', label: 'New Document' },
            ],
            defaultValue: ['new-inquiry', 'new-case', 'new-call-log', 'new-appointment'],
          },
        ],
      },
      {
        type: 'upcoming-appointments',
        name: 'Upcoming Appointments',
        description: 'Your scheduled appointments',
        icon: 'Calendar',
        defaultSize: { width: 4, height: 4 },
        minSize: { width: 3, height: 3 },
        maxSize: { width: 6, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'cases',
        name: 'Cases Overview',
        description: 'Active cases summary',
        icon: 'Briefcase',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'clients',
        name: 'Clients Overview',
        description: 'Client statistics',
        icon: 'Users',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'documents',
        name: 'Documents',
        description: 'Recent document uploads',
        icon: 'FileText',
        defaultSize: { width: 4, height: 4 },
        minSize: { width: 3, height: 3 },
        maxSize: { width: 6, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'inquiry-queue',
        name: 'Inquiry Queue',
        description: 'Pending inquiries',
        icon: 'Inbox',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'messages',
        name: 'Messages',
        description: 'Message center',
        icon: 'MessageSquare',
        defaultSize: { width: 6, height: 5 },
        minSize: { width: 4, height: 4 },
        maxSize: { width: 8, height: 8 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'recent-activity',
        name: 'Recent Activity',
        description: 'Latest activities',
        icon: 'Activity',
        defaultSize: { width: 4, height: 4 },
        minSize: { width: 3, height: 3 },
        maxSize: { width: 6, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
      {
        type: 'stats',
        name: 'Statistics',
        description: 'Key performance metrics',
        icon: 'BarChart',
        defaultSize: { width: 6, height: 4 },
        minSize: { width: 4, height: 3 },
        maxSize: { width: 8, height: 6 },
        defaultConfig: {},
        configSchema: [],
      },
    ];
  }

  async addWidget(userId: string, widgetType: string): Promise<DashboardWidget> {
    const layout = await this.getUserLayout(userId);
    const definition = this.getAvailableWidgets().find(w => w.type === widgetType);
    
    if (!definition) {
      throw new Error(`Widget type ${widgetType} not found`);
    }

    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: widgetType as any,
      title: definition.name,
      position: this.findAvailablePosition(layout.widgets),
      size: definition.defaultSize,
      config: definition.defaultConfig,
      isVisible: true,
      isResizable: true,
      isDraggable: true,
    };

    layout.widgets.push(newWidget);
    await this.saveUserLayout(userId, layout);
    
    return newWidget;
  }

  async removeWidget(userId: string, widgetId: string): Promise<void> {
    const layout = await this.getUserLayout(userId);
    layout.widgets = layout.widgets.filter(w => w.id !== widgetId);
    await this.saveUserLayout(userId, layout);
  }

  async updateWidgetConfig(userId: string, widgetId: string, config: WidgetConfig): Promise<void> {
    const layout = await this.getUserLayout(userId);
    const widget = layout.widgets.find(w => w.id === widgetId);
    
    if (widget) {
      widget.config = { ...widget.config, ...config };
      await this.saveUserLayout(userId, layout);
    }
  }

  async updateWidgetPosition(userId: string, widgetId: string, position: { x: number; y: number }): Promise<void> {
    const layout = await this.getUserLayout(userId);
    const widget = layout.widgets.find(w => w.id === widgetId);
    
    if (widget) {
      widget.position = position;
      await this.saveUserLayout(userId, layout);
    }
  }

  async updateWidgetSize(userId: string, widgetId: string, size: { width: number; height: number }): Promise<void> {
    const layout = await this.getUserLayout(userId);
    const widget = layout.widgets.find(w => w.id === widgetId);
    
    if (widget) {
      widget.size = size;
      await this.saveUserLayout(userId, layout);
    }
  }

  private getDefaultLayout(): DashboardLayout {
    return {
      widgets: [
        {
          id: 'default-call-metrics',
          type: 'call-metrics',
          title: 'Call Metrics',
          position: { x: 0, y: 0 },
          size: { width: 6, height: 4 },
          config: {
            showTrends: true,
            chartType: 'donut',
            refreshInterval: 300000,
            showComparisons: true,
          },
          isVisible: true,
          isResizable: true,
          isDraggable: true,
        },
        {
          id: 'default-inquiry-summary',
          type: 'inquiry-summary',
          title: 'Inquiry Summary',
          position: { x: 6, y: 0 },
          size: { width: 6, height: 4 },
          config: {
            showLeadScores: true,
            sortBy: 'leadScore',
            maxItems: 10,
          },
          isVisible: true,
          isResizable: true,
          isDraggable: true,
        },
        {
          id: 'default-recent-messages',
          type: 'recent-messages',
          title: 'Recent Messages',
          position: { x: 0, y: 4 },
          size: { width: 4, height: 4 },
          config: {
            conversationLimit: 5,
            showUnreadOnly: false,
            autoRefresh: true,
          },
          isVisible: true,
          isResizable: true,
          isDraggable: true,
        },
        {
          id: 'default-activity-feed',
          type: 'activity-feed',
          title: 'Activity Feed',
          position: { x: 4, y: 4 },
          size: { width: 4, height: 4 },
          config: {
            timeRange: 'week',
            maxActivities: 15,
          },
          isVisible: true,
          isResizable: true,
          isDraggable: true,
        },
        {
          id: 'default-quick-actions',
          type: 'quick-actions',
          title: 'Quick Actions',
          position: { x: 8, y: 4 },
          size: { width: 4, height: 4 },
          config: {
            actions: ['new-inquiry', 'new-case', 'new-call-log', 'new-appointment'],
          },
          isVisible: true,
          isResizable: true,
          isDraggable: true,
        },
      ],
      gridSize: { columns: 12, rows: 12 },
      theme: 'light',
      lastModified: new Date().toISOString(),
      version: 1,
    };
  }

  private findAvailablePosition(widgets: DashboardWidget[]): { x: number; y: number } {
    // Simple algorithm to find an available position
    // In a real implementation, you might want a more sophisticated approach
    const gridWidth = 12;
    const occupied = new Set<string>();
    
    widgets.forEach(widget => {
      for (let x = widget.position.x; x < widget.position.x + widget.size.width; x++) {
        for (let y = widget.position.y; y < widget.position.y + widget.size.height; y++) {
          occupied.add(`${x},${y}`);
        }
      }
    });

    // Find first available 4x3 space
    for (let y = 0; y < 20; y++) {
      for (let x = 0; x <= gridWidth - 4; x++) {
        let canPlace = true;
        for (let dx = 0; dx < 4 && canPlace; dx++) {
          for (let dy = 0; dy < 3 && canPlace; dy++) {
            if (occupied.has(`${x + dx},${y + dy}`)) {
              canPlace = false;
            }
          }
        }
        if (canPlace) {
          return { x, y };
        }
      }
    }

    // Fallback to bottom of layout
    const maxY = Math.max(...widgets.map(w => w.position.y + w.size.height), 0);
    return { x: 0, y: maxY };
  }
}